import { Link } from "react-router-dom";
import { buildHomeHeroStats, getHomePulseOptions } from "../lib/homeHero";
import { getMarketOptionColors } from "../lib/marketOptionColors";
import { formatMarketTokenAmount } from "../lib/marketTokenDisplay.js";
import { formatCurrentUtcLine } from "../lib/timeDisplay";

export function HomeHero({ featuredMarket, activeMarkets = [], upcomingMarkets = [], leaderboards = {}, currentUtc, marketsLoaded = true }) {
  const stats = buildHomeHeroStats({ activeMarkets, upcomingMarkets, leaderboards });

  return (
    <section className="home-hero home-mobile-rail">
      <div className="home-hero-main">
        <div className="home-hero-copy">
          <p className="home-hero-eyebrow">Live participation markets</p>
          <h1 className="home-hero-title">Stop waiting for things to happen. Make them happen.</h1>
          <p className="home-hero-description">
            Create a market, rally your people, and put real weight behind the side you want to win. Every vote moves the board.
          </p>

          <div className="home-hero-actions">
            {featuredMarket ? (
              <a href="#active-market" className="theme-button-primary home-hero-primary magic-rainbow-button">
                Enter the live board
              </a>
            ) : (
              <Link to="/markets" className="theme-button-primary home-hero-primary magic-rainbow-button">
                Enter the live board
              </Link>
            )}
            <Link to="/create" className="theme-button-secondary home-hero-secondary magic-rainbow-button">
              Create a market
            </Link>
          </div>

          <div className="home-hero-stats">
            {stats.map((stat) => (
              <div key={`${stat.value}-${stat.label}`} className="home-hero-stat">
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </div>
            ))}
          </div>

          <p className="home-hero-meta">{formatCurrentUtcLine(currentUtc)}</p>
        </div>

        <HomePulseCard featuredMarket={featuredMarket} marketsLoaded={marketsLoaded} />
      </div>
      <div className="home-hero-strip" aria-label="Why participate now">
        <div>
          <strong>Live markets</strong>
          <span>Jump in while votes can still move the result.</span>
        </div>
        <div>
          <strong>Live board</strong>
          <span>Every vote updates the board in real time.</span>
        </div>
        <div>
          <strong>Create markets</strong>
          <span>Launch a market and give people a side to rally behind.</span>
        </div>
        <div>
          <strong>Leaderboards</strong>
          <span>Climb the board and make your calls visible.</span>
        </div>
      </div>
    </section>
  );
}

function HomePulseCard({ featuredMarket, marketsLoaded }) {
  const pulseOptions = getHomePulseOptions(featuredMarket);

  return (
    <aside className="home-pulse-card" aria-label="Featured market pulse">
      <div className="home-pulse-inner">
        <p className="home-pulse-status">
          <span>
            <i aria-hidden="true" />
            Every vote updates the board in real time.
          </span>
        </p>
        {!marketsLoaded ? (
          <div className="home-pulse-loading" aria-hidden>
            <h2 className="home-pulse-placeholder">Loading the live board.</h2>
            <p className="home-pulse-placeholder">Market momentum is coming into view.</p>
            <ul className="home-pulse-options">
              {[72, 48, 29, 14].map((width, index) => (
                <li key={width} className="home-pulse-option home-pulse-placeholder">
                  <span className="home-pulse-fill" style={{ width: `${width}%` }} aria-hidden />
                  <span className="home-pulse-option-content">
                    <span>Option {index + 1}</span>
                    <span>-- SOL (--%)</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : featuredMarket ? (
          <>
            <h2>{featuredMarket.title}</h2>
            <p>{featuredMarket.summary}</p>
            <ul className="home-pulse-options">
              {pulseOptions.map((option, index) => {
                const colors = getMarketOptionColors(index);
                const fillWidth = Math.min(100, Math.max(4, option.percentage));
                return (
                  <li
                    key={option.id}
                    className="home-pulse-option"
                    style={{
                      "--home-pulse-start": colors.gradientStart,
                      "--home-pulse-mid": colors.gradientMid,
                      "--home-pulse-end": colors.gradientEnd
                    }}
                  >
                    <span className="home-pulse-fill" style={{ width: `${fillWidth}%` }} aria-hidden />
                    <span className="home-pulse-option-content">
                      <span>{option.label}</span>
                      <span>
                        {formatMarketTokenAmount(option.votes, featuredMarket, {
                          units: option.totalVoteUnits ?? option.total_vote_units ?? null
                        })} ({option.percentage}%)
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </>
        ) : (
          <div className="home-pulse-empty">
            <h2>Waiting for the next crowd signal.</h2>
            <p>Create the next board or scan what is queued. The first live vote gives everyone something to chase.</p>
            <Link to="/create" className="home-pulse-empty-link">
              Launch a market
            </Link>
          </div>
        )}
      </div>
    </aside>
  );
}
